"use client";

import { m, useReducedMotion } from "motion/react";
import { type ElementType, type ReactNode } from "react";

import {
  LANDING_MOTION_DURATION,
  LANDING_MOTION_EASE,
  LANDING_MOTION_Y,
} from "@/lib/motion/landing-presets";

type StaggerFadeInUpTag = "div" | "section" | "article" | "ul" | "ol";

const MOTION_TAG_MAP = {
  div: m.div,
  section: m.section,
  article: m.article,
  ul: m.ul,
  ol: m.ol,
} satisfies Record<StaggerFadeInUpTag, ElementType>;

export interface StaggerFadeInUpProps {
  children: ReactNode;
  className?: string;
  as?: StaggerFadeInUpTag;
  delay?: number; // ms
  staggerChildren?: number; // ms
  duration?: number;
  distance?: number;
  style?: React.CSSProperties;
}

/**
 * Shared staggered entrance for card/list containers.
 * Child nodes will inherit the same fade-up motion rhythm.
 */
export function StaggerFadeInUp({
  children,
  className,
  as = "div",
  delay = 0,
  staggerChildren = 80,
  duration = LANDING_MOTION_DURATION,
  distance = LANDING_MOTION_Y,
  style,
}: StaggerFadeInUpProps) {
  const shouldReduceMotion = useReducedMotion();
  const MotionTag = MOTION_TAG_MAP[as];
  const delaySeconds = delay / 1000;
  const staggerSeconds = staggerChildren / 1000;

  return (
    <MotionTag
      className={className}
      style={style}
      initial="hidden"
      animate="visible"
      variants={
        shouldReduceMotion
          ? {
              hidden: {},
              visible: {
                transition: { delayChildren: delaySeconds, staggerChildren: 0 },
              },
            }
          : {
              hidden: {},
              visible: {
                transition: {
                  delayChildren: delaySeconds,
                  staggerChildren: staggerSeconds,
                },
              },
            }
      }
    >
      {Array.isArray(children) ? (
        children.map((child, index) => (
          <m.div
            key={index}
            variants={
              shouldReduceMotion
                ? {
                    hidden: { opacity: 1, y: 0 },
                    visible: { opacity: 1, y: 0 },
                  }
                : {
                    hidden: { opacity: 0, y: distance },
                    visible: { opacity: 1, y: 0 },
                  }
            }
            transition={{ duration, ease: LANDING_MOTION_EASE }}
          >
            {child}
          </m.div>
        ))
      ) : (
        <m.div
          variants={
            shouldReduceMotion
              ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
              : {
                  hidden: { opacity: 0, y: distance },
                  visible: { opacity: 1, y: 0 },
                }
          }
          transition={{ duration, ease: LANDING_MOTION_EASE }}
        >
          {children}
        </m.div>
      )}
    </MotionTag>
  );
}
